
/* ==========================================================================
   PI Product Video Slider
   - Shopify video play / pause
   - Only one video plays at a time
   - Click anywhere on video to toggle play/pause
   - Play button hides while playing
   - YouTube video support
   - Swiper navigation arrows hide when sliding is not required
   - Responsive navigation visibility
   - Shopify theme editor compatible
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('pi-p-product-video-slider')) return;

  class PiPProductVideoSlider extends HTMLElement {
    constructor() {
      super();

      this.swiper = null;
      this.swiperElement = null;

      this.videoListeners = [];
      this.navigationListeners = [];

      this.handleVideoToggle = this.handleVideoToggle.bind(this);
      this.handleVideoPlay = this.handleVideoPlay.bind(this);
      this.handleVideoPause = this.handleVideoPause.bind(this);
      this.handleVideoEnded = this.handleVideoEnded.bind(this);
      this.handleMediaClick = this.handleMediaClick.bind(this);
      this.handleYoutubeClick = this.handleYoutubeClick.bind(this);
      this.handleSlideChange = this.handleSlideChange.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.initSwiper();
      this.bindVideoEvents();
      this.bindClickEvents();
      this.bindNavigation();
    }

    disconnectedCallback() {
      this.unbindVideoEvents();
      this.unbindClickEvents();
      this.unbindNavigation();

      window.removeEventListener('resize', this.handleResize);

      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.destroy(true, true);
      }

      this.swiper = null;
      this.swiperElement = null;
    }

    /* --------------------------------------------------
       Swiper initialization
    -------------------------------------------------- */

    initSwiper() {
      this.swiperElement = this.querySelector(
        '.pi-p-product-video-slider__swiper'
      );

      if (!this.swiperElement) return;

      if (typeof window.Swiper !== 'undefined') {
        this.createSwiper();
      } else {
        this.loadSwiper();
      }
    }

    loadSwiper() {
      const existingScript = document.querySelector(
        'script[data-pi-product-video-swiper]'
      );

      if (existingScript) {
        if (typeof window.Swiper !== 'undefined') {
          this.createSwiper();
        } else {
          existingScript.addEventListener(
            'load',
            () => this.createSwiper(),
            { once: true }
          );
        }

        return;
      }

      const script = document.createElement('script');

      script.src =
        'https://cdn.jsdelivr.net/npm/swiper@11.1.14/swiper-bundle.min.js';

      script.dataset.piProductVideoSwiper = 'true';

      script.onload = () => this.createSwiper();

      script.onerror = () => {
        console.error(
          'Unable to load Swiper for product video slider.'
        );
      };

      document.head.appendChild(script);
    }

    createSwiper() {
      if (
        !this.isConnected ||
        !this.swiperElement ||
        typeof window.Swiper === 'undefined' ||
        this.swiper
      ) {
        return;
      }

      this.swiper = new window.Swiper(this.swiperElement, {
        slidesPerView: 2,
        spaceBetween: 16,

        watchOverflow: true,
        observer: true,
        observeParents: true,
        watchSlidesProgress: true,

        breakpoints: {
          750: {
            slidesPerView: 2,
            spaceBetween: 16
          },

          990: {
            slidesPerView: 2,
            spaceBetween: 16
          },

          1440: {
            slidesPerView: 3,
            spaceBetween: 16
          }
        },

        on: {
          init: () => {
            this.updateNavigationVisibility();
          },

          slideChange: this.handleSlideChange,

          resize: () => {
            this.updateNavigationVisibility();
          },

          breakpoint: () => {
            this.updateNavigationVisibility();
          },

          update: () => {
            this.updateNavigationVisibility();
          },

          lock: () => {
            this.updateNavigationVisibility();
          },

          unlock: () => {
            this.updateNavigationVisibility();
          }
        }
      });

      window.addEventListener('resize', this.handleResize);

      this.updateNavigationVisibility();
    }

    /* --------------------------------------------------
       Hide arrows when all slides are visible
    -------------------------------------------------- */

    updateNavigationVisibility() {
      if (!this.swiper || this.swiper.destroyed) return;

      const prevButton = this.querySelector(
        '.pi-p-product-video-slider__prev'
      );

      const nextButton = this.querySelector(
        '.pi-p-product-video-slider__next'
      );

      if (!prevButton || !nextButton) return;

      /*
       * isLocked is true when Swiper cannot slide because
       * all slides fit inside the available slider width.
       */
      const shouldHide =
        this.swiper.isLocked ||
        this.swiper.slides.length <=
          this.swiper.params.slidesPerView;

      prevButton.hidden = shouldHide;
      nextButton.hidden = shouldHide;

      if (shouldHide) {
        prevButton.setAttribute('aria-hidden', 'true');
        nextButton.setAttribute('aria-hidden', 'true');
      } else {
        prevButton.removeAttribute('aria-hidden');
        nextButton.removeAttribute('aria-hidden');
      }
    }

    handleResize() {
      if (!this.swiper || this.swiper.destroyed) return;

      this.swiper.update();
      this.updateNavigationVisibility();
    }

    /* --------------------------------------------------
       Navigation buttons
    -------------------------------------------------- */

    bindNavigation() {
      const nextButton = this.querySelector(
        '.pi-p-product-video-slider__next'
      );

      const prevButton = this.querySelector(
        '.pi-p-product-video-slider__prev'
      );

      if (nextButton) {
        nextButton.addEventListener(
          'click',
          this.handleNextClick
        );

        this.navigationListeners.push({
          element: nextButton,
          event: 'click',
          handler: this.handleNextClick
        });
      }

      if (prevButton) {
        prevButton.addEventListener(
          'click',
          this.handlePrevClick
        );

        this.navigationListeners.push({
          element: prevButton,
          event: 'click',
          handler: this.handlePrevClick
        });
      }
    }

    unbindNavigation() {
      this.navigationListeners.forEach((listener) => {
        listener.element.removeEventListener(
          listener.event,
          listener.handler
        );
      });

      this.navigationListeners = [];
    }

    handleNextClick() {
      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.slideNext();
      }
    }

    handlePrevClick() {
      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.slidePrev();
      }
    }

    /* --------------------------------------------------
       Video event binding
    -------------------------------------------------- */

    bindVideoEvents() {
      this.querySelectorAll(
        '.pi-p-product-video-slider__media video'
      ).forEach((video) => {
        video.addEventListener('play', this.handleVideoPlay);
        video.addEventListener('pause', this.handleVideoPause);
        video.addEventListener('ended', this.handleVideoEnded);

        this.videoListeners.push(video);
      });
    }

    unbindVideoEvents() {
      this.videoListeners.forEach((video) => {
        video.removeEventListener('play', this.handleVideoPlay);
        video.removeEventListener('pause', this.handleVideoPause);
        video.removeEventListener('ended', this.handleVideoEnded);
      });

      this.videoListeners = [];
    }

    bindClickEvents() {
      this.addEventListener('click', this.handleMediaClick);
      this.addEventListener('click', this.handleYoutubeClick);
    }

    unbindClickEvents() {
      this.removeEventListener('click', this.handleMediaClick);
      this.removeEventListener('click', this.handleYoutubeClick);
    }

    /* --------------------------------------------------
       Toggle video by clicking video or play button
    -------------------------------------------------- */

    handleMediaClick(event) {
      const video = event.target.closest(
        '.pi-p-product-video-slider__media video'
      );

      if (video && this.contains(video)) {
        this.toggleVideo(video, event);
        return;
      }

      const button = event.target.closest(
        '.pi-p-product-video-slider__play-toggle'
      );

      if (!button || !this.contains(button)) return;

      const media = button.closest(
        '.pi-p-product-video-slider__media'
      );

      if (!media) return;

      const mediaVideo = media.querySelector('video');

      if (!mediaVideo) return;

      this.toggleVideo(mediaVideo, event);
    }

    toggleVideo(video, event) {
      if (!(video instanceof HTMLVideoElement)) return;

      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const media = video.closest(
        '.pi-p-product-video-slider__media'
      );

      if (video.paused || video.ended) {
        this.pauseAllVideos(video);

        const playPromise = video.play();

        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            if (media) {
              media.classList.remove('is-playing');
            }
          });
        }
      } else {
        video.pause();

        if (media) {
          media.classList.remove('is-playing');
        }
      }
    }

    handleVideoToggle(event) {
      this.toggleVideo(event.currentTarget, event);
    }

    handleVideoPlay(event) {
      const video = event.currentTarget;

      if (!(video instanceof HTMLVideoElement)) return;

      this.pauseAllVideos(video);

      const media = video.closest(
        '.pi-p-product-video-slider__media'
      );

      if (media) {
        media.classList.add('is-playing');
      }
    }

    handleVideoPause(event) {
      const video = event.currentTarget;

      if (!(video instanceof HTMLVideoElement)) return;

      const media = video.closest(
        '.pi-p-product-video-slider__media'
      );

      if (media) {
        media.classList.remove('is-playing');
      }
    }

    handleVideoEnded(event) {
      const video = event.currentTarget;

      if (!(video instanceof HTMLVideoElement)) return;

      const media = video.closest(
        '.pi-p-product-video-slider__media'
      );

      if (media) {
        media.classList.remove('is-playing');
      }

      video.currentTime = 0;
    }

    pauseAllVideos(exceptVideo) {
      this.querySelectorAll(
        '.pi-p-product-video-slider__media video'
      ).forEach((video) => {
        if (video === exceptVideo) return;

        video.pause();

        const media = video.closest(
          '.pi-p-product-video-slider__media'
        );

        if (media) {
          media.classList.remove('is-playing');
        }
      });
    }

    /* --------------------------------------------------
       YouTube video support
    -------------------------------------------------- */

    handleYoutubeClick(event) {
      const trigger = event.target.closest(
        '[data-pi-youtube-url]'
      );

      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();

      const media = trigger.closest(
        '.pi-p-product-video-slider__media'
      );

      if (!media) return;

      const youtubeUrl = trigger.dataset.piYoutubeUrl;

      if (!youtubeUrl) return;

      let videoId = '';

      try {
        const url = new URL(
          youtubeUrl,
          window.location.origin
        );

        if (url.hostname.includes('youtu.be')) {
          videoId =
            url.pathname.split('/').filter(Boolean)[0] || '';
        } else if (url.pathname.includes('/embed/')) {
          videoId =
            url.pathname.split('/embed/')[1].split('/')[0];
        } else {
          videoId =
            url.searchParams.get('v') ||
            url.pathname.split('/').filter(Boolean).pop() ||
            '';
        }
      } catch (error) {
        console.warn('Invalid YouTube URL:', youtubeUrl);
        return;
      }

      if (!videoId) return;

      this.pauseAllVideos(null);

      this.querySelectorAll(
        '.pi-p-product-video-slider__iframe'
      ).forEach((iframe) => {
        iframe.src = 'about:blank';

        const iframeMedia = iframe.closest(
          '.pi-p-product-video-slider__media'
        );

        if (iframeMedia) {
          iframeMedia.classList.remove('is-playing');

          const oldTrigger = iframeMedia.querySelector(
            '[data-pi-youtube-url]'
          );

          if (oldTrigger) {
            oldTrigger.hidden = false;
          }
        }
      });

      let iframe = media.querySelector(
        '.pi-p-product-video-slider__iframe'
      );

      if (!iframe) {
        iframe = document.createElement('iframe');

        iframe.className =
          'pi-p-product-video-slider__iframe';

        iframe.title = 'Product video';

        iframe.allow =
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

        iframe.allowFullscreen = true;
        iframe.loading = 'lazy';

        const container = media.querySelector(
          '.pi-p-product-video-slider__youtube'
        ) || media;

        container.appendChild(iframe);
      }

      iframe.src =
        'https://www.youtube-nocookie.com/embed/' +
        encodeURIComponent(videoId) +
        '?autoplay=1&rel=0';

      media.classList.add('is-playing');
      trigger.hidden = true;
    }

    /* --------------------------------------------------
       Slide change: pause videos outside active slide
    -------------------------------------------------- */

    handleSlideChange() {
      if (!this.swiper || this.swiper.destroyed) return;

      this.updateNavigationVisibility();

      const visibleSlides = this.swiper.slides.filter((slide) => {
        return slide.classList.contains('swiper-slide-visible');
      });

      this.querySelectorAll(
        '.pi-p-product-video-slider__media video'
      ).forEach((video) => {
        const slide = video.closest('.swiper-slide');

        if (!visibleSlides.includes(slide)) {
          video.pause();

          const media = video.closest(
            '.pi-p-product-video-slider__media'
          );

          if (media) {
            media.classList.remove('is-playing');
          }
        }
      });

      this.querySelectorAll(
        '.pi-p-product-video-slider__iframe'
      ).forEach((iframe) => {
        const slide = iframe.closest('.swiper-slide');

        if (!visibleSlides.includes(slide)) {
          iframe.src = 'about:blank';

          const media = iframe.closest(
            '.pi-p-product-video-slider__media'
          );

          if (media) {
            media.classList.remove('is-playing');

            const trigger = media.querySelector(
              '[data-pi-youtube-url]'
            );

            if (trigger) {
              trigger.hidden = false;
            }
          }
        }
      });
    }
  }

  customElements.define(
    'pi-p-product-video-slider',
    PiPProductVideoSlider
  );
})();
